import {useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {
  groupNotifications,
  useMarkNotificationRead,
  useMarkNotificationsRead,
  useNotificationNavigate,
  useNotifications,
  useUnreadNotificationsCount,
  type Notification,
  type NotificationGroup,
} from '@/hooks/producto/notificaciones/use-notifications'
import {useAuth} from '@/hooks/platform/use-auth'
import {BellGlyph} from '@/components/ui/icons'
import {Tag} from '@/components/ui/tag'
import {formatDate} from '@/lib/risk'
import {cn} from '@/lib/utils'

const SEVERITY_TONE: Record<string, string> = {
  info: 'bg-secondary text-muted-foreground',
  warn: 'tone-amber',
  urgent: 'tone-red',
}

function NoticeRow({
  notice,
  admin,
  onOpen,
}: {
  notice: Notification
  admin: boolean
  onOpen: (notice: Notification) => void
}) {
  const {t} = useTranslation()
  const unread = notice.read_at === null
  const toneClass = SEVERITY_TONE[notice.severity] ?? SEVERITY_TONE.info
  return (
    <button
      type="button"
      data-testid="notice-row"
      data-kind={notice.kind}
      data-source-id={notice.source_id ?? undefined}
      onClick={() => onOpen(notice)}
      className={cn(
        'flex w-full items-start gap-2 px-3 py-2.5 text-left',
        admin ? 'hover:bg-white/5' : 'hover:bg-secondary'
      )}>
      <span
        aria-hidden="true"
        className={cn('mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full', unread ? 'bg-primary' : 'bg-transparent')}
      />
      <span className="min-w-0 flex-1">
        <span className={cn('block text-[13px] leading-snug', unread ? 'font-medium' : 'text-muted-foreground')}>
          {notice.message}
        </span>
        <span className="mt-0.5 flex items-center gap-1.5">
          <span
            data-testid="notice-severity"
            className={cn('rounded px-1 py-0.5 font-mono text-[9px] tracking-wider uppercase', toneClass)}>
            {t(`notif.severity.${notice.severity}`)}
          </span>
          <span className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
            {formatDate(notice.created_at)}
          </span>
        </span>
      </span>
    </button>
  )
}

function NoticeGroupRow({
  group,
  admin,
  onOpen,
}: {
  group: NotificationGroup
  admin: boolean
  onOpen: (group: NotificationGroup) => void
}) {
  const {t} = useTranslation()
  const head = group.items[0]
  const unread = group.items.some((n) => n.read_at === null)
  const toneClass = SEVERITY_TONE[head.severity] ?? SEVERITY_TONE.info
  return (
    <button
      type="button"
      data-testid="notice-group-row"
      data-kind={group.kind}
      data-group-count={group.items.length}
      onClick={() => onOpen(group)}
      className={cn(
        'flex w-full items-start gap-2 px-3 py-2.5 text-left',
        admin ? 'hover:bg-white/5' : 'hover:bg-secondary'
      )}>
      <span
        aria-hidden="true"
        className={cn('mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full', unread ? 'bg-primary' : 'bg-transparent')}
      />
      <span className="min-w-0 flex-1">
        <span className={cn('block text-[13px] leading-snug', unread ? 'font-medium' : 'text-muted-foreground')}>
          {head.message}
        </span>
        <span className="mt-0.5 flex flex-wrap items-center gap-1.5">
          <Tag tone={admin ? 'inverse' : 'neutral'} data-testid="notice-group-count">
            {t('notifications.groupCount', {count: group.items.length})}
          </Tag>
          <span
            data-testid="notice-severity"
            className={cn('rounded px-1 py-0.5 font-mono text-[9px] tracking-wider uppercase', toneClass)}>
            {t(`notif.severity.${head.severity}`)}
          </span>
          <span className="font-mono text-[10px] tracking-wider text-muted-foreground uppercase">
            {formatDate(head.created_at)}
          </span>
        </span>
      </span>
    </button>
  )
}

/** `admin` only tints the rows for the dark surface; the top-bar trigger always sits on the light bar. */
export function NotificationsBell({admin = false, placement = 'up'}: {admin?: boolean; placement?: 'up' | 'down'}) {
  const {t} = useTranslation()
  const {user} = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const down = placement === 'down'
  const {data, refetch} = useNotifications(true)
  const {data: unreadCount = 0, refetch: refetchUnread} = useUnreadNotificationsCount(true)
  const markRead = useMarkNotificationRead()
  const markGroupRead = useMarkNotificationsRead()
  const goTo = useNotificationNavigate()

  const notices = data?.items ?? []
  const groups = groupNotifications(notices)

  const openNotice = (notice: Notification) => {
    if (notice.read_at === null) markRead.mutate(notice.id)
    void goTo(notice)
    setOpen(false)
  }

  const openGroup = (group: NotificationGroup) => {
    const unread = group.items.filter((notice) => notice.read_at === null).map((notice) => notice.id)
    if (unread.length > 0) markGroupRead.mutate(unread)
    void goTo(group.items[0])
    setOpen(false)
  }

  return (
    <div className="relative">
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className={cn(
              'absolute right-0 z-50 flex max-h-[70vh] w-72 flex-col overflow-hidden rounded-xl border border-border bg-popover text-foreground shadow-pop sm:max-h-96',
              down ? 'top-full mt-2' : 'bottom-full left-0 mb-2'
            )}>
            <div className="flex shrink-0 items-baseline justify-between gap-2 border-b border-border px-3 py-2 font-mono text-[10px] font-semibold tracking-wider text-muted-foreground uppercase">
              <span>{t('notifications.title')}</span>
              {data && data.total > 0 && (
                <span data-testid="notifications-preview-count" className="font-normal normal-case">
                  {t('table.showing', {start: 1, end: notices.length, total: data.total})}
                </span>
              )}
            </div>
            <div className="max-h-72 flex-1 overflow-y-auto">
              {notices.length === 0 ? (
                <p className="px-3 py-4 text-[13px] text-muted-foreground">{t('notifications.empty')}</p>
              ) : (
                groups.map((group) =>
                  group.items.length === 1 ? (
                    <NoticeRow key={group.key} notice={group.items[0]} admin={admin} onOpen={openNotice} />
                  ) : (
                    <NoticeGroupRow key={group.key} group={group} admin={admin} onOpen={openGroup} />
                  )
                )
              )}
            </div>
            {!user?.email && (
              <p
                className="shrink-0 border-t border-border px-3 py-2 text-[11.5px] text-muted-foreground"
                data-testid="notifications-email-nudge">
                {t('notif.emailNudge')}
              </p>
            )}
            <button
              type="button"
              data-testid="notifications-see-all"
              onClick={() => {
                setOpen(false)
                navigate('/notifications')
              }}
              className="shrink-0 border-t border-border px-3 py-2.5 text-left text-[13px] font-medium text-primary hover:bg-secondary">
              {t('notifications.seeAll')}
            </button>
          </div>
        </>
      )}
      {down ? (
        <button
          type="button"
          data-testid="notifications-bell"
          aria-label={t('notifications.open')}
          aria-expanded={open}
          aria-haspopup="menu"
          onClick={() => {
            // The background poll is 60s: opening refetches so a just-happened event shows up.
            if (!open) {
              refetch()
              refetchUnread()
            }
            setOpen(!open)
          }}
          className="topbar-link relative flex h-11 w-11 cursor-pointer items-center justify-center outline-none focus-visible:ring-2 focus-visible:ring-ring/50 md:h-9 md:w-9">
          <BellGlyph />
          {unreadCount > 0 && (
            <span
              data-testid="notifications-unread"
              className="absolute top-0.5 right-0.5 inline-flex min-w-4 items-center justify-center rounded-full bg-primary px-1 py-0.5 font-mono text-[9px] font-semibold text-primary-foreground">
              {unreadCount}
            </span>
          )}
        </button>
      ) : (
        <button
          type="button"
          data-testid="notifications-bell"
          aria-label={t('notifications.open')}
          aria-expanded={open}
          aria-haspopup="menu"
          onClick={() => {
            if (!open) {
              refetch()
              refetchUnread()
            }
            setOpen(!open)
          }}
          className={cn(
            'relative flex w-full cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
            admin
              ? 'text-white/70 hover:bg-white/10 hover:text-white'
              : 'text-muted-foreground hover:bg-secondary/60 hover:text-foreground'
          )}>
          <BellGlyph />
          <span className="text-sm font-medium">{t('notifications.title')}</span>
          {unreadCount > 0 && (
            <span
              data-testid="notifications-unread"
              className="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-primary px-1.5 py-0.5 font-mono text-[10px] font-semibold text-primary-foreground">
              {unreadCount}
            </span>
          )}
        </button>
      )}
    </div>
  )
}
